import { useClient } from '@cortex/react-query';
import {
  formantBotAgentId,
  formantBotVoices,
  randomFormantBotAvatar,
  type FormantBot,
  type FormantBotAvatar,
  type FormantBotVoice,
  type PersistentAgentHeartbeat,
} from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { Modal } from '../common/Modal';

import { BotAvatar, resolveBotAvatar } from './BotAvatar';

async function readHttpError(err: unknown): Promise<string> {
  const fallback = err instanceof Error ? err.message : 'Could not save';
  const response = (err as { response?: Response } | null)?.response;
  if (!response) return fallback;
  try {
    const body = (await response.clone().json()) as { message?: unknown };
    if (Array.isArray(body.message)) return body.message.map(String).join('; ');
    if (typeof body.message === 'string' && body.message) return body.message;
  } catch {
    // The body was not JSON.
  }
  return fallback;
}

const cronPresets: { label: string; expr: string }[] = [
  { label: 'Every 15 min', expr: '*/15 * * * *' },
  { label: 'Hourly', expr: '0 * * * *' },
  { label: 'Daily 09:00', expr: '0 9 * * *' },
];

/**
 * Understudy-style bot configuration: extra instructions, a saved voice, and
 * schedules. Schedules are the thread's existing heartbeats. Voice is stored
 * and is not played on the thread page.
 */
export function BotSettingsDialog({
  bot,
  creating,
  onClose,
}: {
  /** Set when editing an existing bot. */
  bot: FormantBot | null;
  /** Set when the New bot dialog is open. */
  creating: boolean;
  onClose: () => void;
}) {
  const client = useClient();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [name, setName] = useState('');
  const [instructions, setInstructions] = useState('');
  const [draftSchedules, setDraftSchedules] = useState<{ cron: string; prompt: string }[]>([]);
  const [voice, setVoice] = useState<FormantBotVoice>('marin');
  const [avatar, setAvatar] = useState<FormantBotAvatar>(randomFormantBotAvatar());
  const [prompt, setPrompt] = useState('Check in and take any pending actions.');
  const [cron, setCron] = useState(cronPresets[1]?.expr ?? '0 * * * *');
  const [error, setError] = useState<string | null>(null);

  const open = creating || bot !== null;

  useEffect(() => {
    setName(bot?.name ?? '');
    setInstructions(bot?.instructions ?? '');
    setVoice(bot?.voice ?? 'marin');
    setAvatar(bot ? resolveBotAvatar(bot.avatar, bot.id) : randomFormantBotAvatar());
    setDraftSchedules([]);
    setPrompt('Check in and take any pending actions.');
    setCron(cronPresets[1]?.expr ?? '0 * * * *');
    setError(null);
  }, [bot, creating]);

  const heartbeatUrl = bot
    ? `v1/persistent-agent-groups/${formantBotAgentId}/agents/${bot.persistentAgentId}/heartbeats`
    : null;

  const heartbeats = useQuery({
    queryKey: ['formant-bot-heartbeats', bot?.persistentAgentId],
    enabled: Boolean(heartbeatUrl),
    queryFn: () => {
      if (!heartbeatUrl) throw new Error('No bot thread');
      return client.http.get(heartbeatUrl).json<PersistentAgentHeartbeat[]>();
    },
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        instructions: instructions.trim() || null,
        voice,
        avatar,
      };
      if (creating) {
        return client.http
          .post('v1/formant-bots', { json: { ...body, schedules: draftSchedules } })
          .json<FormantBot>();
      }
      if (!bot) throw new Error('No bot to update');
      return client.http.patch(`v1/formant-bots/${bot.id}`, { json: body }).json<FormantBot>();
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['formant-bots'] });
      onClose();
    },
    onError: (err: unknown) => {
      readHttpError(err)
        .then((message) => setError(message))
        .catch(() => setError('Could not save'));
    },
  });

  const remove = useMutation({
    mutationFn: () => {
      if (!bot) throw new Error('No bot to delete');
      return client.http.delete(`v1/formant-bots/${bot.id}`);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['formant-bots'] });
      if (bot && pathname.includes(bot.persistentAgentId)) {
        navigate({ to: '/agents' }).catch(() => undefined);
      }
      onClose();
    },
    onError: (err: unknown) => {
      readHttpError(err)
        .then((message) => setError(message))
        .catch(() => setError('Could not delete'));
    },
  });

  const addHeartbeat = useMutation({
    mutationFn: () => {
      if (!heartbeatUrl) throw new Error('No bot thread');
      return client.http
        .post(heartbeatUrl, { json: { cron, prompt, label: 'Schedule' } })
        .json<PersistentAgentHeartbeat>();
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['formant-bot-heartbeats', bot?.persistentAgentId] });
    },
    onError: (err: Error) => setError(err.message || 'Could not add the schedule'),
  });

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!save.isPending) onClose();
      }}
      title={creating ? 'New bot' : (bot?.name ?? 'Bot')}
      eyebrow="Bot settings"
      width={520}
      footer={
        <>
          {!creating && bot ? (
            <button
              type="button"
              className="btn btn--secondary"
              style={{ marginRight: 'auto' }}
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm(`Delete ${bot.name} and its thread?`)) remove.mutate();
              }}
            >
              Delete
            </button>
          ) : null}
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={save.isPending || name.trim().length === 0}
            onClick={() => save.mutate()}
          >
            {creating ? 'Create' : 'Save'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <BotAvatar avatar={avatar} size={72} title={name.trim() || 'Bot avatar'} />
          <div>
            <div className="field__label">Avatar</div>
            <p className="t-xs t-muted">
              An oscilloscope trace. Generate rolls a new wave and color.
            </p>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => setAvatar(randomFormantBotAvatar())}
            >
              Generate
            </button>
          </div>
        </div>
        <label className="field">
          <span className="field__label">Name</span>
          <input
            className="input"
            value={name}
            autoFocus={true}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
            placeholder="Night shift"
          />
        </label>
        <label className="field">
          <span className="field__label">Instructions</span>
          <textarea
            className="input"
            rows={5}
            maxLength={4000}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="You watch for stalled tasks."
          />
        </label>
        <label className="field">
          <span className="field__label">Voice</span>
          <select
            className="input"
            value={voice}
            onChange={(e) => setVoice(e.target.value as FormantBotVoice)}
          >
            {formantBotVoices.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
          <span className="t-xs t-muted">Saved for later. The thread page does not speak yet.</span>
        </label>
        <div>
          <span className="field__label">Schedules</span>
          <p className="t-xs t-muted">
            A schedule is a heartbeat on this bot’s thread. Each beat wakes the bot with the prompt.
          </p>
          {(creating ? draftSchedules : (heartbeats.data ?? [])).map((beat, index) => {
            const label =
              'label' in beat && typeof beat.label === 'string' ? beat.label : 'Schedule';
            const paused = 'status' in beat && beat.status === 'PAUSED';
            const id = 'id' in beat && typeof beat.id === 'string' ? beat.id : String(index);
            return (
              <div key={id} className="t-sm" style={{ padding: '6px 0' }}>
                {label} · <code>{beat.cron}</code>
                {paused ? ' · paused' : ''}
              </div>
            );
          })}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <select className="input" value={cron} onChange={(e) => setCron(e.target.value)}>
              {cronPresets.map((p) => (
                <option key={p.expr} value={p.expr}>
                  {p.label}
                </option>
              ))}
            </select>
            <input
              className="input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              style={{ flex: 1, minWidth: 160 }}
            />
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              disabled={!prompt.trim() || addHeartbeat.isPending}
              onClick={() => {
                if (creating) {
                  setDraftSchedules((rows) =>
                    [...rows, { cron, prompt: prompt.trim() }].slice(0, 5),
                  );
                  return;
                }
                addHeartbeat.mutate();
              }}
            >
              Add
            </button>
          </div>
        </div>
        {error ? <p className="t-sm">{error}</p> : null}
      </div>
    </Modal>
  );
}
